import React, { useState, useEffect, useRef } from "react";

/**
 * Module-level cache of background image URLs that have already fully loaded at
 * least once this session, so a remount/re-render does not flash the blurred
 * placeholder back over an already-loaded background (which reads as a dark
 * "shadow" wash over the component on hover/re-render).
 */
const loadedBgCache = new Set();

/**
 * Renders the absolutely-positioned, z-0 background layer for a hero/
 * containered component. Shows a rounded shimmer placeholder while the
 * background image loads.
 *
 * ``ambientClass`` is an optional extra class appended to the rendered
 * div — used by hero modes that declare a continuous animation (e.g.
 * Cinematic's ``hero-ambient-ken-burns``) and want it to apply to the
 * background layer itself rather than the content. Safe to omit.
 *
 * ``jsPatternLayer`` is an optional React node rendered as a child of the
 * pattern <div>. Used by JavaScript-powered patterns (see
 * ``renderer/patterns-js``) so they paint inside the same color shell as
 * CSS-only patterns and inherit brightness/opacity utilities.
 */
const Background = ({
  bgImage,
  imageStyleClasses,
  imageInlineStyles,
  colorClass,
  colorStyle,
  jsPatternLayer = null,
  hoverClass = "",
  ambientClass = "",
}) => {
  const [imageLoaded, setImageLoaded] = useState(() =>
    bgImage ? loadedBgCache.has(bgImage) : false
  );
  const prevSrcRef = useRef(null);

  useEffect(() => {
    if (!bgImage) {
      setImageLoaded(false);
      prevSrcRef.current = null;
      return;
    }

    if (bgImage === prevSrcRef.current) return;
    prevSrcRef.current = bgImage;

    // Already loaded this URL before — keep it visible, never flash the placeholder.
    if (loadedBgCache.has(bgImage)) {
      setImageLoaded(true);
      return;
    }
    setImageLoaded(false);

    const img = new window.Image();
    img.src = bgImage;
    const markLoaded = () => {
      loadedBgCache.add(bgImage);
      setImageLoaded(true);
    };
    if (img.complete && img.naturalWidth > 0) {
      markLoaded();
    } else {
      img.onload = markLoaded;
      img.onerror = markLoaded;
    }
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [bgImage]);

  if (bgImage) {
    return (
      <>
        <div
          className={`${imageStyleClasses} absolute inset-0 w-full h-full bg-cover bg-center z-0 transition duration-200 ease-in-out ${hoverClass} ${ambientClass}`}
          style={{
            backgroundImage: `url(${bgImage})`,
            ...imageInlineStyles,
            opacity: imageLoaded ? 1 : 0,
            transition: "opacity 0.35s ease-in-out",
          }}
        />
        <div
            className="absolute inset-0 w-full h-full z-0"
            style={{
              background: "rgba(200, 200, 200, 0.15)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              borderRadius: "inherit",
              overflow: "hidden",
              opacity: imageLoaded ? 0 : 1,
              transition: "opacity 0.3s ease-out",
              pointerEvents: "none",
            }}
            aria-hidden="true"
          >
            <span
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)",
                animation: "progressive-img-shimmer 1.5s ease-in-out infinite",
              }}
            />
          </div>
      </>
    );
  }

  return (
    <div
      className={`${colorClass} absolute inset-0 w-full h-full z-0 transition duration-200 ease-in-out ${hoverClass} ${ambientClass}`}
      style={colorStyle}
    >
      {jsPatternLayer}
    </div>
  );
};

export default Background;
